import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAlert } from "../../../context/AlertContext";
import {
  useTranscriptionConfig,
  useUpdateTranscriptionConfig,
} from "../../../hooks/useTranscriptionConfig";
import {
  REDACTION_CATEGORIES,
  RETENTION_PERIOD_UNITS,
  toFormValues,
  toPutRequest,
  TranscriptionConfigFormValues,
} from "./transcriptionConfigForm";

const TRANSCRIPTION_CONFIG_FORM_ID = "transcription-config-form";

const TranscriptionSettings = () => {
  const { t } = useTranslation(["transcriptionConfig", "common"]);
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const showAlert = useAlert();

  const { data, isLoading } = useTranscriptionConfig(customerId);
  const updateMutation = useUpdateTranscriptionConfig(customerId);

  const form = useForm<TranscriptionConfigFormValues>({
    defaultValues: { categories: [], rules: [] },
  });
  const { control, register, handleSubmit, reset, watch, formState } = form;
  const { isSubmitting, errors } = formState;
  const {
    fields: ruleFields,
    append,
    remove,
    move,
  } = useFieldArray({ control, name: "rules" });
  const categories = watch("categories");

  useEffect(() => {
    if (data) reset(toFormValues(data));
  }, [data, reset]);

  const goBack = () => navigate(-1);

  const submit = async (values: TranscriptionConfigFormValues) => {
    try {
      await updateMutation.mutateAsync(toPutRequest(customerId!, values));
      showAlert(t("transcriptionConfig:saveSuccess"), "success");
    } catch {
      showAlert(t("transcriptionConfig:saveError"), "error");
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form
      id={TRANSCRIPTION_CONFIG_FORM_ID}
      onSubmit={handleSubmit(submit)}
      className="flex m-auto flex-col h-3/4"
    >
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t("transcriptionConfig:redactionCategories")}
          subheader={t("transcriptionConfig:redactionCategoriesDescription")}
        />
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={90}>
                  {t("transcriptionConfig:enabled")}
                </TableCell>
                <TableCell width={160}>
                  {t("transcriptionConfig:category")}
                </TableCell>
                <TableCell>{t("transcriptionConfig:tags")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((row, index) => (
                <TableRow key={row.category}>
                  <TableCell>
                    <Controller
                      control={control}
                      name={`categories.${index}.enabled`}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    {t(`transcriptionConfig:categories.${row.category}`)}
                  </TableCell>
                  <TableCell>
                    {row.enabled ? (
                      <Controller
                        control={control}
                        name={`categories.${index}.tags`}
                        render={({ field }) => (
                          <Autocomplete
                            multiple
                            freeSolo
                            autoSelect
                            options={[]}
                            value={field.value}
                            onChange={(_, value) => field.onChange(value)}
                            renderTags={(value, getTagProps) =>
                              value.map((tag, tagIndex) => (
                                <Chip
                                  label={tag}
                                  size="small"
                                  {...getTagProps({ index: tagIndex })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder={t("transcriptionConfig:addTag")}
                                helperText={t("transcriptionConfig:addTagHelp")}
                              />
                            )}
                          />
                        )}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("transcriptionConfig:enableCategoryForTags")}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={t("transcriptionConfig:retentionRules")}
          subheader={t("transcriptionConfig:retentionRulesDescription")}
        />
        <CardContent>
          {ruleFields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("transcriptionConfig:noRules")}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={130}>
                    {t("transcriptionConfig:priority")}
                  </TableCell>
                  <TableCell width={180}>
                    {t("transcriptionConfig:category")}
                  </TableCell>
                  <TableCell width={130}>
                    {t("transcriptionConfig:retainFor")}
                  </TableCell>
                  <TableCell width={150}>
                    {t("transcriptionConfig:unit")}
                  </TableCell>
                  <TableCell width={60} />
                </TableRow>
              </TableHead>
              <TableBody>
                {ruleFields.map((field, index) => (
                  <TableRow key={field.id}>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" fontWeight={500}>
                          {index + 1}
                        </Typography>
                        <Tooltip title={t("transcriptionConfig:moveUp")}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === 0}
                              onClick={() => move(index, index - 1)}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t("transcriptionConfig:moveDown")}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={index === ruleFields.length - 1}
                              onClick={() => move(index, index + 1)}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Controller
                        control={control}
                        name={`rules.${index}.category`}
                        render={({ field: categoryField }) => (
                          <TextField
                            {...categoryField}
                            select
                            size="small"
                            fullWidth
                          >
                            {REDACTION_CATEGORIES.map((category) => (
                              <MenuItem key={category} value={category}>
                                {t(
                                  `transcriptionConfig:categories.${category}`,
                                )}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        inputProps={{ min: 1 }}
                        error={!!errors.rules?.[index]?.durationAfterStartDate}
                        helperText={
                          errors.rules?.[index]?.durationAfterStartDate
                            ? t("transcriptionConfig:durationRequired")
                            : undefined
                        }
                        {...register(`rules.${index}.durationAfterStartDate`, {
                          required: true,
                          min: 1,
                          valueAsNumber: true,
                        })}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        control={control}
                        name={`rules.${index}.unit`}
                        render={({ field: unitField }) => (
                          <TextField
                            {...unitField}
                            select
                            size="small"
                            fullWidth
                          >
                            {RETENTION_PERIOD_UNITS.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {t(`transcriptionConfig:units.${unit}`)}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t("transcriptionConfig:deleteRule")}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => remove(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Button
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={() =>
              append({
                category: "Financial",
                durationAfterStartDate: 1,
                unit: "Days",
              })
            }
          >
            {t("transcriptionConfig:addRule")}
          </Button>
        </CardContent>
      </Card>

      <Box className="sticky bottom-0 bg-white h-fit z-50 mt-4">
        <Divider />
        <Stack className="p-2" direction="row" spacing={2}>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {t("common:save")}
          </Button>
          <Button onClick={goBack} color="secondary" variant="contained">
            {t("common:cancel")}
          </Button>
        </Stack>
      </Box>
    </form>
  );
};

export default TranscriptionSettings;
